import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { warrantyApi } from '../api/warrantyApi';
import type { WarrantyFilters } from '../types/warranty.types';
import type {
  AssignWarrantyGcDto,
  BulkAssignWarrantyGcEntry,
} from '@/features/societies/types/generalConditions.types';

// Re-export mutations pour import facile depuis useWarranties
export {
  useActivateWarranty,
  useDeactivateWarranty,
  useDuplicateWarranty,
} from './useWarrantyMutations';

/**
 * Valeurs distinctes pour les filtres (facets)
 */
export const useWarrantyFilterFacets = (societyId?: string) => {
  return useQuery({
    queryKey: ['warranty-filter-facets', societyId],
    queryFn: () => warrantyApi.getFilterFacets(societyId),
    staleTime: 60000,
  });
};

/**
 * Liste des garanties avec filtres
 */
export const useWarranties = (filters?: WarrantyFilters) => {
  return useQuery({
    queryKey: ['warranties', filters],
    queryFn: () => warrantyApi.getWarranties(filters),
    staleTime: 30000,
  });
};

/**
 * Toutes les garanties disponibles pour une société (propres + héritées)
 */
export const useAvailableWarranties = (societyId: string | undefined) => {
  return useQuery({
    queryKey: ['available-warranties', societyId],
    queryFn: () => warrantyApi.getAvailableWarranties(societyId!),
    enabled: !!societyId,
    staleTime: 30000,
  });
};

/**
 * Preview des provisions d'une société
 */
export const useProvisionsPreview = (societyId: string | undefined) => {
  return useQuery({
    queryKey: ['provisions-preview', societyId],
    queryFn: () => warrantyApi.getProvisionsPreview(societyId!),
    enabled: false,
    staleTime: 0,
  });
};

/**
 * Mettre à jour les provisions pour une société
 */
export const useUpdateWarrantyProvisions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (societyId: string) => warrantyApi.updateProvisionsForSociety(societyId),
    onSuccess: (_data, societyId) => {
      queryClient.invalidateQueries({ queryKey: ['warranties-by-society', societyId] });
      queryClient.invalidateQueries({ queryKey: ['available-warranties', societyId] });
    },
  });
};

// ==================== Conditions Générales par garantie ====================

/**
 * Liste des versions de CG éligibles pour une garantie + variante.
 * Inclut le défaut Affinicar dans `defaultVersion`.
 */
export const useWarrantyGcOptions = (
  warrantyUuid: string | undefined,
  variant: 'cash' | 'stripe',
  enabled = true,
) => {
  return useQuery({
    queryKey: ['warranty-gc-options', warrantyUuid, variant],
    queryFn: () => warrantyApi.getGcOptions(warrantyUuid!, variant),
    enabled: !!warrantyUuid && enabled,
    staleTime: 30000,
  });
};

/**
 * Met à jour les FKs CG d'une garantie individuelle (cash et/ou stripe).
 *
 * Invalidation cache :
 * - `['warranty', uuid]` : la garantie elle-même (FKs mises à jour)
 * - `['warranties']` : listes de garanties (badge / colonne « version assignée »)
 * - `['warranty-gc-options', uuid]` : dropdown du bloc d'assignation
 * - `['warranty-pdf', uuid]` : preview PDF principale dans la vue garantie
 *   (sinon le PDF reste affiché avec l'ancienne version assignée jusqu'à un F5)
 */
export const useAssignWarrantyGc = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, dto }: { uuid: string; dto: AssignWarrantyGcDto }) =>
      warrantyApi.assignGeneralConditions(uuid, dto),
    onSuccess: (_data, { uuid }) => {
      queryClient.invalidateQueries({ queryKey: ['warranty', uuid] });
      queryClient.invalidateQueries({ queryKey: ['warranties'] });
      queryClient.invalidateQueries({ queryKey: ['warranty-gc-options', uuid] });
      queryClient.invalidateQueries({ queryKey: ['warranty-pdf', uuid] });
    },
  });
};

/**
 * Bulk : assigne en masse les FKs CG (cash et/ou stripe) à plusieurs garanties.
 * Utilisé par le modal d'assignation global de l'onglet Conditions.
 *
 * Le backend valide chaque entry (warrantyType + variante + société). Les entries
 * invalides sont skippées sans bloquer les autres.
 */
export const useBulkAssignWarrantyGc = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assignments: BulkAssignWarrantyGcEntry[]) =>
      warrantyApi.bulkAssignGeneralConditions(assignments),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warranties'] });
      queryClient.invalidateQueries({ queryKey: ['warranty'] });
      queryClient.invalidateQueries({ queryKey: ['warranty-pdf'] });
      queryClient.invalidateQueries({ queryKey: ['warranty-gc-options'] });
    },
  });
};
